import * as React from 'react';

import { useTheme } from '../context/ThemeContext';
import { petDog } from '../utils/petDog';

// Easter egg: in dark mode, a tiny dog with a flashlight occasionally trots
// along the bottom of the nav bar. Hover the dog and it stops; click to pet it.
// Decorative only (aria-hidden) and skipped for users who prefer reduced motion.

const JUST_ENABLED_DELAY_MS = 2500; // first walk shortly after switching to dark mode
const MIN_DELAY_MS = 45_000;
const MAX_DELAY_MS = 150_000;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

const randomDelay = () => MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);

export const NightWalk = () => {
  const { isDark } = useTheme();
  const [isWalking, setIsWalking] = React.useState(false);
  // Bumped to re-arm the timer when it fired while the tab was hidden.
  const [retryTick, setRetryTick] = React.useState(0);
  const prevIsDarkRef = React.useRef(isDark);
  const justEnabledRef = React.useRef(false);

  React.useEffect(() => {
    if (isDark && !prevIsDarkRef.current) justEnabledRef.current = true;
    if (!isDark) setIsWalking(false);
    prevIsDarkRef.current = isDark;
  }, [isDark]);

  React.useEffect(() => {
    if (!isDark || isWalking || prefersReducedMotion()) return;
    const delay = justEnabledRef.current ? JUST_ENABLED_DELAY_MS : randomDelay();
    const timer = window.setTimeout(() => {
      if (document.hidden) {
        setRetryTick((tick) => tick + 1);
        return;
      }
      justEnabledRef.current = false;
      setIsWalking(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isDark, isWalking, retryTick]);

  if (!isDark || !isWalking) return null;

  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-[22px] overflow-hidden pointer-events-none"
    >
      <div
        className="night-walk-track"
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget) setIsWalking(false);
        }}
      >
        <svg
          className="night-walk-dog"
          width="96"
          height="22"
          viewBox="0 0 96 22"
          // Only .night-walk-body receives clicks (see easter-eggs.css); hovering it pauses the walk.
          onClick={(event) => petDog(event, event.currentTarget)}
        >
          <defs>
            <linearGradient id="night-walk-beam" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#fff3c4" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#fff3c4" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Flashlight beam, ahead of the dog */}
          <polygon points="31,10.6 96,1 96,22 31,12.6" fill="url(#night-walk-beam)" />
          <g className="night-walk-body">
            <path
              className="night-walk-tail"
              d="M6 10.5 Q2.5 7.5 3.2 4.2"
              fill="none"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <g className="night-walk-legs-a">
              <rect x="7" y="13.5" width="1.7" height="6.5" rx="0.8" />
              <rect x="21.4" y="13.5" width="1.7" height="6.5" rx="0.8" />
            </g>
            <g className="night-walk-legs-b">
              <rect x="9.4" y="13.5" width="1.7" height="6.5" rx="0.8" />
              <rect x="19" y="13.5" width="1.7" height="6.5" rx="0.8" />
            </g>
            <ellipse cx="14.5" cy="11.8" rx="9" ry="4" />
            <polygon points="19,10 22,5.8 25.5,8.5 21.5,13.5" />
            <circle cx="24.3" cy="7.8" r="3.6" />
            <ellipse cx="27.6" cy="9.1" rx="2.3" ry="1.6" />
            <polygon points="21.6,5.4 22.8,2.2 24.6,5.2" />
            {/* Flashlight held in the mouth */}
            <rect
              className="night-walk-flashlight"
              x="28.4"
              y="9.4"
              width="3"
              height="2.4"
              rx="0.5"
            />
            <rect className="night-walk-lens" x="31" y="9.6" width="0.9" height="2" />
          </g>
        </svg>
      </div>
    </div>
  );
};
